import { ChevronRight, Copy, FolderTree, Hash, Search } from "lucide-react";
import { useMemo, useState } from "react";
import type { TaxonomyCategory } from "../../types/categories";

// ── Single tree node ───────────────────────────────────────
const TreeNode = ({
	node,
	selectedId,
	onSelect,
	depth = 0,
}: {
	node: TaxonomyCategory;
	selectedId: number | null;
	onSelect: (cat: TaxonomyCategory) => void;
	depth?: number;
}) => {
	const [expanded, setExpanded] = useState(depth < 1);
	const hasChildren = node.children.length > 0;
	const isSelected = selectedId === node.id;

	return (
		<div>
			<button
				type="button"
				onClick={() => {
					onSelect(node);
					if (hasChildren) setExpanded(!expanded);
				}}
				className={`w-full flex items-center gap-2 px-3 py-2 text-left rounded-lg transition-all duration-150 group ${
					isSelected
						? "bg-primary/10 text-primary"
						: "text-slate-600 hover:bg-slate-50 hover:text-slate-800"
				}`}
				style={{ paddingLeft: `${depth * 20 + 12}px` }}
			>
				{/* Expand chevron */}
				<span
					className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${
						hasChildren ? "" : "invisible"
					} ${expanded ? "rotate-90" : ""}`}
				>
					<ChevronRight className="w-3.5 h-3.5" />
				</span>

				{/* Icon */}
				{hasChildren ? (
					<FolderTree
						className={`w-4 h-4 shrink-0 ${isSelected ? "text-primary" : "text-slate-400 group-hover:text-slate-500"}`}
					/>
				) : (
					<Hash
						className={`w-3.5 h-3.5 shrink-0 ${isSelected ? "text-primary" : "text-slate-300 group-hover:text-slate-400"}`}
					/>
				)}

				{/* Label */}
				<span className="text-[13px] font-medium truncate flex-1">
					{node.name}
				</span>

				{/* ID badge */}
				<span
					className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
						isSelected
							? "bg-primary/20 text-primary"
							: "bg-slate-100 text-slate-400 group-hover:bg-slate-200 group-hover:text-slate-500"
					}`}
				>
					{node.id}
				</span>

				{/* Children count */}
				{hasChildren && (
					<span className="text-[10px] text-slate-400">
						{node.children.length}
					</span>
				)}
			</button>

			{/* Children */}
			{expanded && hasChildren && (
				<div>
					{node.children.map((child) => (
						<TreeNode
							key={child.id}
							node={child}
							selectedId={selectedId}
							onSelect={onSelect}
							depth={depth + 1}
						/>
					))}
				</div>
			)}
		</div>
	);
};

// ── Main tree component ────────────────────────────────────
export const TaxonomyTree = ({
	tree,
	flatList,
	selectedId,
	onSelect,
}: {
	tree: TaxonomyCategory[];
	flatList: TaxonomyCategory[];
	selectedId: number | null;
	onSelect: (cat: TaxonomyCategory) => void;
}) => {
	const [search, setSearch] = useState("");
	const [copied, setCopied] = useState(false);

	// Get selected category object
	const selectedCategory = useMemo(
		() => flatList.find((c) => c.id === selectedId),
		[flatList, selectedId],
	);

	// Handle copy to clipboard
	const handleCopy = async () => {
		if (!selectedCategory) return;
		const path = selectedCategory.fullPath.replace(/ \/ /g, " > ");
		try {
			await navigator.clipboard.writeText(path);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error("Failed to copy:", err);
		}
	};

	const filtered = useMemo(() => {
		if (!search.trim()) return null;
		const q = search.toLowerCase();
		return flatList.filter(
			(c) =>
				c.name.toLowerCase().includes(q) ||
				c.fullPath.toLowerCase().includes(q) ||
				c.id.toString().includes(q),
		);
	}, [search, flatList]);

	return (
		<div className="flex flex-col h-full">
			{/* Search */}
			<div className="relative px-4 pt-4 pb-3">
				<Search className="absolute left-7 top-1/2 -translate-y-1/2 mt-0.5 w-4 h-4 text-slate-400" />
				<input
					type="text"
					placeholder="Search categories or ID..."
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200/80 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/30 focus:bg-white transition-all"
				/>
			</div>

			{/* Copy button for selected category */}
			{selectedCategory && (
				<div className="px-4 pb-3">
					<div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3 flex items-start gap-2">
						<div className="flex-1 min-w-0">
							<p className="text-[11px] text-slate-500 font-medium mb-1">
								Selected path
							</p>
							<p className="text-[13px] text-slate-700 truncate">
								{selectedCategory.fullPath.replace(/ \/ /g, " > ")}
							</p>
						</div>
						<button
							type="button"
							onClick={handleCopy}
							className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-600 hover:text-slate-800 hover:border-slate-300 hover:shadow-sm transition-all text-[12px] font-medium shrink-0"
						>
							<Copy className="w-3.5 h-3.5" />
							{copied ? "Copied!" : "Copy"}
						</button>
					</div>
				</div>
			)}

			{/* Tree / Search results */}
			<div className="flex-1 overflow-y-auto px-2 pb-4 scrollbar-thin">
				{filtered ? (
					<>
						<p className="px-3 py-2 text-[11px] text-slate-400 font-medium">
							{filtered.length} result{filtered.length !== 1 ? "s" : ""}
						</p>
						{filtered.length === 0 ? (
							<div className="text-center py-8 text-slate-400 text-sm">
								No categories found
							</div>
						) : (
							filtered.map((cat) => (
								<button
									key={cat.id}
									type="button"
									onClick={() => {
										onSelect(cat);
										setSearch("");
									}}
									className={`w-full flex flex-col gap-0.5 px-3 py-2.5 rounded-lg text-left transition-colors ${
										selectedId === cat.id
											? "bg-primary/10"
											: "hover:bg-slate-50"
									}`}
								>
									<span className="text-[13px] font-medium text-slate-700 truncate">
										{cat.name}
										<span className="ml-2 text-[10px] font-mono text-slate-400">
											#{cat.id}
										</span>
									</span>
									<span className="text-[11px] text-slate-400 truncate">
										{cat.fullPath}
									</span>
								</button>
							))
						)}
					</>
				) : (
					tree.map((root) => (
						<TreeNode
							key={root.id}
							node={root}
							selectedId={selectedId}
							onSelect={onSelect}
						/>
					))
				)}
			</div>
		</div>
	);
};
